import Props from '@service-victoria/component-library/props';
import { isEmpty } from 'lodash-es';
import * as Yup from 'yup';

import { constants, date as dateUtils } from 'utils';

import { isDDMMYYYY, isMMYYYY } from './date';

export const date = {
  /**
   * Generate a schema for validating a date of birth field
   * @param mode format of the given date ('Full' or 'MonthYear')
   * @param extraAllValidation append extra validations to the date `all` field once all generic fields have been validated successfully
   */
  dateOfBirth: (
    mode: Props.DateMode,
    divider = constants.Dates.DATE_DIVIDER_CHAR
  ): Yup.MixedSchema<any> => {
    return Yup.mixed()
      .test('day valid', constants.DateValidationErrors.day, function validate(value: string) {
        if (mode === 'FullDate') {
          if (!value) {
            return false;
          }
          const day = value.split(divider)[0];
          return date.sections.day(day);
        }
        return true;
      })
      .test('month valid', constants.DateValidationErrors.month, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const month = mode === 'FullDate' ? parts[1] : parts[0];
        return date.sections.month(month);
      })
      .test('year valid', constants.DateValidationErrors.year, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const year = mode === 'FullDate' ? parts[2] : parts[1];
        return date.sections.year(year);
      })
      .test(
        'isValidDate',
        constants.DateValidationErrors.invalidDate,
        function validate(value: string) {
          return !value ? false : mode === 'FullDate' ? isDDMMYYYY(value) : isMMYYYY(value);
        }
      )
      .test(
        'minimum date',
        'Sorry, you must be over 15 years old to continue.',
        function validate(value: string) {
          // Don't validate the maximum if not full date was entered
          if (!value) {
            return false;
          }
          const maxDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -15, 'y', 'FullDate');
          return dateUtils.compare(`${value}`, maxDate, 'FullDate') <= 0;
        }
      )
      .test(
        'maximum date',
        `That doesn’t look right. Please enter a year after ${
          dateUtils
            .add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate')
            .split(constants.Dates.DATE_DIVIDER_CHAR)[2]
        }`,
        function validate(value: string) {
          if (!value) {
            return false;
          }
          const minDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate');
          return !!value && dateUtils.compare(value, minDate, 'FullDate') >= 0;
        }
      );
  },

  dateOfEvent: (
    mode: Props.DateMode,
    divider = constants.Dates.DATE_DIVIDER_CHAR
  ): Yup.MixedSchema<any> => {
    return Yup.string()
      .test('day valid', constants.DateValidationErrors.day, function validate(value: string) {
        if (mode === 'FullDate') {
          if (!value) {
            return false;
          }
          const day = value.split(divider)[0];
          return date.sections.day(day);
        }
        return true;
      })
      .test('month valid', constants.DateValidationErrors.month, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const month = mode === 'FullDate' ? parts[1] : parts[0];
        return date.sections.month(month);
      })
      .test('year valid', constants.DateValidationErrors.year, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const year = mode === 'FullDate' ? parts[2] : parts[1];
        return date.sections.year(year);
      })
      .test(
        'isValidDate',
        constants.DateValidationErrors.invalidDate,
        function validate(value: string) {
          return !value ? false : mode === 'FullDate' ? isDDMMYYYY(value) : isMMYYYY(value);
        }
      )
      .test(
        'maximum date',
        `That doesn’t look right. Please enter a year after ${
          dateUtils
            .add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate')
            .split(constants.Dates.DATE_DIVIDER_CHAR)[2]
        }`,
        function validate(value: string) {
          if (!value) {
            return false;
          }
          const minDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate');
          return !!value && dateUtils.compare(value, minDate, 'FullDate') >= 0;
        }
      )
      .test(
        'date is before today',
        'That doesn’t look right. The date of event can’t be before today.',
        function validate(value: string) {
          // Don't validate the maximum if not full date was entered
          if (!value) {
            return false;
          }
          const now = dateUtils.NowAsString('FullDate');
          return !!value && dateUtils.compare(value, now, 'FullDate') >= 0;
        }
      );
  },

  /**
   * Date validatiors functions for any part of the date
   */
  sections: {
    day: (value: string): boolean => {
      return !isEmpty(value) && value.length === 2 && /(0[1-9]|[12]\d|3[01])/.test(value);
    },
    month: (value: string): boolean => {
      return !isEmpty(value) && value.length === 2 && /(0[1-9]|1[012])/.test(value);
    },
    year: (value: string): boolean => {
      return !isEmpty(value) && value.length === 4;
    },
    all: (mode: Props.DateMode, value: string): boolean => {
      return mode === 'FullDate' ? isDDMMYYYY(value) : isMMYYYY(value);
    }
  }
};

export const generic = {
  isNumber: (val?: unknown): boolean => {
    // empty string is parsed as valid so need to check this edge case
    return val !== '' && !isNaN(Number(val));
  }
};

/**
 * Validate credit card number
 * @param cardNumber credit card number as string of numbers
 */
const isCardNumberValid = (cardNumber: string) => {
  let sum = 0;
  let alt = false;

  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let num = parseInt(cardNumber.charAt(i), 10);

    if (alt) {
      num = num * 2;
      if (num > 9) {
        num = num - 9;
      }
    }

    alt = !alt;
    sum = sum + num;
  }

  return sum % 10 === 0;
};

/**
 * Credit card validation check
 * @param name form field name
 */
export const creditCard = Yup.object().shape<Props.CreditCard.CardData>({
  cardholderName: Yup.string().required(constants.GenericValidationErrors.required),
  cardNumber: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'invalidCardNumber',
      'The credit card number you entered wasn’t recognised. Please enter a valid credit card number.',
      function validate(cardNumber: string) {
        return isCardNumberValid(String(cardNumber).replace(/\s/g, ''));
      }
    ),
  cvv: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .matches(
      /\d{3}/,
      'Your CVC/CVV is 3 numbers on the back of your card, usually next to your signature.'
    ),
  expiryDate: Yup.string()
    .required('Your expiry date is 4 numbers in MM/YY format.')
    .test(
      'isPastDate',
      'Your credit card looks like it’s expired. Please check the expiry and try again or use a different card.',
      function validate(value: string) {
        const year = value.split(constants.Dates.DATE_DIVIDER_CHAR)[1];
        const month = value.split(constants.Dates.DATE_DIVIDER_CHAR)[0];
        const date = new Date(parseInt(`20${year ?? '00'}`), parseInt(month ?? '1'));
        return new Date() < date;
      }
    ),
  saveForLater: Yup.boolean().required(constants.GenericValidationErrors.required)
});

/**
 * merge multiple schemas together
 * @param schemas collection of schemas
 */
export function mergeSchemas(...schemas: Yup.Schema<unknown>[]): Yup.Schema<unknown> {
  const [first, ...rest] = schemas;

  const merged = rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first);

  return merged;
}

/**
 *  Checks whether an address is PO Box. Returns true the string contains "po box"
 */
export const isPostOfficeBox = (addressString: string) => {
  const cleanedAddressString = addressString.toLowerCase().replace(/[^\w\s]/gi, '');
  return cleanedAddressString.indexOf('po box') >= 0;
};

// Inspired from: https://github.com/mathiasbynens/emoji-regex/blob/main/src/index.js
export const doesNotContainEmoji = (text?: string) => {
  return !text?.match(/\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}/gu);
};

export const Regex = {
  POSTCODE: /^(?:(?:[2-8]\d|9[0-7]|0?[28]|0?9(?=09))(?:\d{2}))$/,
  // Allowing "-" and "'" in the suburb because there are a few suburbs
  // that contains this special character (e.g: Mil-Lel, Mount Kuring-gai, O'Reilly, O'Connor)

  SUBURB: /^[a-zA-Z-' ]+$/,

  EMAIL:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,

  NAME: /(^([a-zA-Z]+([-—–’']?[a-zA-Z]+)? ?)+$)/,

  PERMIT_NO: /^\d{4}\/\d{8}\/\d$/
};

// See https://abr.business.gov.au/Help/AbnFormat
const WEIGTHING_FACTOR = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

export const validateAbn = (abn: number): boolean => {
  const digits = Array.from(abn.toString()).map(Number);

  if (digits.length !== 11) {
    return false;
  }

  digits[0] = digits[0] - 1;

  const total = digits
    .map((digit, index) => digit * WEIGTHING_FACTOR[index])
    .reduce((result, digit) => result + digit, 0);

  return total % 89 === 0;
};
